/**
 * Zustand-based chat hook with stable selectors
 * This is the proper implementation that doesn't cause infinite loops
 */

import React, { useCallback, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { WORKER_API_URL } from '../../api/config';
import { useAuth } from '../useAuth';
import { fetchWithRetry } from '../../utils/fetchWithRetry';
import { 
  useStableMessages, 
  useHydrationStatus, 
  useStreamStatus, 
  useChatActions 
} from './useStableChatSelectors';
import { conversationService } from '../../api/conversationService';
import { threadService } from '../../api/threadService';
import { useChatStore } from '../../stores/chatStore';

interface ZustandChatOptions {
  conversationId: string;
  onStreamStart?: () => void;
  onStreamEnd?: () => void;
  onStreamError?: (error: Error) => void;
}

export function useZustandChat({ 
  conversationId, 
  onStreamStart, 
  onStreamEnd, 
  onStreamError 
}: ZustandChatOptions) {
  const { token } = useAuth();
  
  // Track the previous conversation ID to detect changes
  const prevConversationIdRef = React.useRef<string | undefined>(conversationId);
  
  // Clear conversation data when switching to a different conversation
  React.useEffect(() => {
    if (prevConversationIdRef.current && prevConversationIdRef.current !== conversationId && conversationId) {
      console.log('[useZustandChat] Conversation changed, clearing old data:', {
        from: prevConversationIdRef.current,
        to: conversationId
      });
      
      // Clear the old conversation's messages from the store
      const clearConversation = useChatStore.getState().clearConversation;
      if (prevConversationIdRef.current) {
        clearConversation(prevConversationIdRef.current);
      }
    }
    prevConversationIdRef.current = conversationId;
  }, [conversationId]);
  
  // If no conversation ID, still call hooks but they will safely return defaults
  const isNewChat = !conversationId || conversationId === '';
  
  // Stable selectors (unconditional to obey Rules of Hooks)
  const messages = useStableMessages(conversationId);
  const hydrationStatus = useHydrationStatus(conversationId);
  const streamStatus = useStreamStatus();
  const actions = useChatActions();
  // Track existing message count to allow re-fetch if store has none
  const existingCount = useChatStore((state) => {
    try {
      const map = (state as any)?.messageIdsByConvRef || {};
      const ids = (map as Record<string, unknown>)[conversationId];
      return Array.isArray(ids) ? ids.length : 0;
    } catch {
      return 0;
    }
  });
  
  // Initial data fetch with TanStack Query
  const messagesQuery = useQuery({
    queryKey: ['messages', conversationId],
    queryFn: async () => {
      // Don't fetch for new chat
      if (!conversationId || conversationId === '' || !token) return [];
      
      const response = await fetchWithRetry(
        `${WORKER_API_URL}/api/v1/conversations/${conversationId}?include=messages&limit=200`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          maxRetries: 3,
          initialDelay: 300,
        }
      );
      
      if (!response.ok) {
        throw new Error(`Failed to fetch messages: ${response.status}`);
      }
      
      const data = await response.json();

      // Branching read-path: if conversation has main_thread_id, hydrate from ThreadView
      try {
        const convObj = (data && (data.conversation || (data.data && data.data.conversation))) || null;
        const mainThreadId = convObj?.main_thread_id as string | null | undefined;
        if (mainThreadId) {
          const view = await threadService.getThreadView(mainThreadId);
          const now = Date.now();
          const storeMessages = (view?.messages || []).map((m: any, idx: number) => ({
            id: m.id,
            clientId: m.id,
            conversationRef: conversationId,
            role: (m.role === 'tool' ? 'system' : (m.role as any)),
            content: (m?.content && (m as any).content?.text) || JSON.stringify(m?.content || {}),
            createdAt: new Date(now + idx).toISOString(),
            status: 'saved' as const,
          }));
          return storeMessages;
        }
      } catch (e) {
        // fall through to legacy path
      }

      // Robust extraction: handle multiple shapes and worker proxy quirks
      let list: any[] = [];
      try {
        if (Array.isArray((data as any)?.messages)) {
          list = (data as any).messages;
        } else if (Array.isArray((data as any)?.data?.messages)) {
          list = (data as any).data.messages;
        } else if (Array.isArray((data as any)?.conversation?.messages)) {
          list = (data as any).conversation.messages;
        } else if (Array.isArray(data)) {
          // In case the worker returned the messages array directly
          list = data as any[];
        } else if (
          (data as any)?.messages &&
          (Array.isArray((data as any).messages.items) || Array.isArray((data as any).messages.results))
        ) {
          list = (data as any).messages.items || (data as any).messages.results || [];
        }
      } catch {
        // ignore, will fallback below
      }

      // Fallback: if list is empty or not an array, fetch direct messages endpoint
      if (!Array.isArray(list) || list.length === 0) {
        try {
          const fallback = await fetchWithRetry(
            `${WORKER_API_URL}/api/v1/conversations/${conversationId}/messages/?skip=0&limit=200`,
            {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json',
              },
              maxRetries: 2,
              initialDelay: 300,
            }
          );
          if (fallback.ok) {
            const fbData = await fallback.json();
            if (Array.isArray(fbData)) {
              list = fbData;
            }
          }
        } catch (e) {
          // Non-fatal; show empty state if truly no messages
          try { console.warn('[useZustandChat] Fallback messages fetch failed:', e); } catch {}
        }
      }

      // Normalize and ensure stable identifiers; sort ascending by time/id
      const normalized = (Array.isArray(list) ? list : [])
        .slice()
        .sort((a, b) => {
          const taRaw = new Date(a.timestamp || a.created_at || 0).getTime();
          const tbRaw = new Date(b.timestamp || b.created_at || 0).getTime();
          const ta = Number.isFinite(taRaw) ? taRaw : Number.POSITIVE_INFINITY;
          const tb = Number.isFinite(tbRaw) ? tbRaw : Number.POSITIVE_INFINITY;
          if (ta !== tb) return ta - tb;
          const iaRaw = Number(a.message_id ?? a.id);
          const ibRaw = Number(b.message_id ?? b.id);
          const ia = Number.isFinite(iaRaw) ? iaRaw : Number.POSITIVE_INFINITY;
          const ib = Number.isFinite(ibRaw) ? ibRaw : Number.POSITIVE_INFINITY;
          if (ia !== ib) return ia - ib;
          // Fallback to string compare for UUIDs
          const sa = String(a.id ?? a.message_id ?? '');
          const sb = String(b.id ?? b.message_id ?? '');
          return sa.localeCompare(sb);
        })
        .map((msg: any) => {
          const serverId = msg.message_id ?? msg.id;
          const id = serverId != null ? String(serverId) : undefined;
          const clientId = id
            || (msg.client_id != null ? String(msg.client_id) : `msg-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
          return {
            id,
            clientId,
            conversationRef: conversationId,
            role: (msg.sender_role ?? msg.role) as 'user' | 'assistant' | 'system',
            content: msg.message_content ?? msg.content ?? '',
            createdAt: String(msg.timestamp ?? msg.created_at ?? new Date().toISOString()),
            status: 'saved' as const,
          };
        });

      return normalized;
    },
    enabled: !!conversationId && conversationId !== '' && !!token && (hydrationStatus === 'idle' || existingCount === 0),
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    throwOnError: false,
    refetchOnMount: false,
  });
  
  // Hydrate Zustand from TanStack Query (once)
  useEffect(() => {
    // Skip hydration for new chat
    if (isNewChat) return;
    if (!messagesQuery.data || hydrationStatus !== 'idle') return;
    
    actions.setHydrationStatus(conversationId, 'loading');
    actions.hydrateConversation(
      conversationId,
      messagesQuery.data,
      false,
      undefined
    );
    actions.setHydrationStatus(conversationId, 'ready');
  }, [messagesQuery.data, conversationId, hydrationStatus, isNewChat]); // actions is stable, no need in deps
  
  // Send message function
  const sendMessage = useCallback((text: string, overrideConversationId?: string, opts?: { threadId?: string; parentMessageId?: string | null; sideStream?: boolean; attachments?: Array<{ documentId: string; mimeType?: string; originalFilename?: string; size?: number }> }) => {
    const convIdToUse = overrideConversationId || conversationId;
    
    const hasText = !!text.trim();
    const hasAttachments = Array.isArray(opts?.attachments) && (opts!.attachments as any[]).length > 0;
    console.log('[useZustandChat] 📤 SEND MESSAGE CALLED:', {
      textLength: text.length,
      textPreview: text.substring(0, 100),
      conversationId: convIdToUse,
      hasOverride: !!overrideConversationId,
      hasAttachments,
      timestamp: new Date().toISOString()
    });

    if ((!hasText && !hasAttachments) || !convIdToUse) {
      console.error('[useZustandChat] ❌ Invalid message or conversation ID:', {
        hasText,
        hasAttachments,
        hasConvId: !!convIdToUse
      });
      return;
    }
    
    // Add user message unless this is a side (split) stream
    let userMessageId: string | undefined;
    if (!opts?.sideStream && hasText) {
      console.log('[useZustandChat] 👤 Adding user message draft...');
      userMessageId = actions.addUserMessageDraft(convIdToUse, text);
      console.log('[useZustandChat] ✅ User message draft added:', { userMessageId });
    }

    // Persist user message in background (non-blocking), except for branching conversations
    (async () => {
      try {
        // Determine if this conversation is branching (has main_thread_id)
        let isBranching = false;
        try {
          const conv = await conversationService.getConversationById(convIdToUse);
          isBranching = Boolean((conv as any)?.main_thread_id);
        } catch {}

        if (!opts?.sideStream && hasText) {
          // Bring back legacy user-message persistence for continuity (analytics/compat),
          // but avoid reconciling to chat_messages ID in branching convs to prevent
          // id-mismatch vs ThreadView hydration.
          console.log('[useZustandChat] 💾 Persisting user message (legacy)');
          const created = await conversationService.createChatMessage(convIdToUse, {
            conversation_id: 0 as any,
            sender_role: 'user' as any,
            message_content: text,
          });
          console.log('[useZustandChat] ✅ User message persisted (legacy):', {
            messageId: created.message_id,
            userMessageId,
            isBranching
          });
          // Only reconcile to legacy ID when not branching; in branching, allow
          // ThreadView hydration to upgrade the draft to the thread message UUID.
          if (!isBranching) {
            const reconcile = useChatStore.getState().reconcileMessageId;
            if (userMessageId) reconcile(userMessageId, String(created.message_id));
          }
        }
      } catch (e) {
        console.error('[useZustandChat] ❌ Failed user message persistence check:', e);
      }
    })();
    
    // Start streaming (UnifiedSSEProviderV3 will handle the actual API call)
    console.log('[useZustandChat] 🔄 Starting stream...');
    const assistantDraftId = actions.startStream(convIdToUse, { threadId: opts?.threadId, parentMessageId: opts?.parentMessageId ?? null, sideStream: !!opts?.sideStream, pendingUserText: opts?.sideStream ? text : undefined, attachments: opts?.attachments });
    console.log('[useZustandChat] ✅ Stream started:', { assistantDraftId });
    
    if (onStreamStart) {
      console.log('[useZustandChat] 🎯 Calling onStreamStart callback');
      onStreamStart();
    }
    
    console.log('[useZustandChat] ✅ MESSAGE SEND COMPLETE:', {
      conversationId: convIdToUse,
      userMessageId,
      assistantDraftId,
      timestamp: new Date().toISOString()
    });
    
    return { userMessageId, assistantDraftId };
  }, [conversationId, onStreamStart]); // actions is stable, no need in deps
  
  // Monitor stream status
  useEffect(() => {
    if (!streamStatus.isStreaming && streamStatus.conversationRef === conversationId) {
      if (streamStatus.error && onStreamError) {
        onStreamError(new Error(streamStatus.error));
      } else if (onStreamEnd) {
        onStreamEnd();
      }
    }
  }, [streamStatus, conversationId, onStreamEnd, onStreamError]);
  
  const isLoading = messagesQuery.isLoading || hydrationStatus === 'loading';
  const isStreaming = streamStatus.isStreaming && streamStatus.conversationRef === conversationId;
  const isSending = isStreaming && messages.some(m => m.isDraft && m.role === 'user');

  // Prefer streaming error for current conversation if present
  const combinedError = (streamStatus.conversationRef === conversationId && streamStatus.error)
    ? new Error(streamStatus.error)
    : (messagesQuery.error as any);
  
  return {
    messages,
    sendMessage,
    isLoading,
    isSending,
    isStreaming,
    error: combinedError,
    isHydrated: hydrationStatus === 'ready',
    refetch: messagesQuery.refetch,
  };
}
